import { useState, type FormEvent } from "react";
import PageHero from "@/components/feature/PageHero";

const contactMethods = ["Phone call", "WhatsApp message", "Email", "Text me first"];

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState<string>("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError("");
    const form = e.currentTarget;
    const fd = new FormData(form);

    const trap = String(fd.get("company_alt") || "").trim();
    if (trap) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("company_alt");

    const photo = fd.get("photo");
    if (photo instanceof File && photo.size === 0) fd.delete("photo");

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (value instanceof File) {
        if (value.size > 0) payload.append(key, `Uncollectable: ${value.name}`);
      } else {
        payload.append(key, String(value));
      }
    });

    try {
      setSubmitting(true);
      const response = await fetch("https://readdy.ai/api/form/d96svg7pg5pqhofcusl0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg
            ? `Sorry — ${serverMsg}. Please try again or send an email.`
            : "Sorry — something went wrong sending your message. Please try again."
        );
      }
    } catch {
      setStatus("error");
      setFormError("Sorry — network issue. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title={
          <>
            Get in touch,<br />
            <em className="not-italic text-accent-400">it's easy.</em>
          </>
        }
        intro="Need a local electrician? Send a message with a few details and I'll get back to you as soon as possible."
        image="https://readdy.ai/api/search-image?query=Warm%20interior%20of%20a%20London%20flat%20hallway%20with%20a%20small%20wooden%20side%20table%20holding%20a%20phone%20and%20keys%20next%20to%20a%20black%20front%20door%2C%20soft%20natural%20morning%20light%2C%20realistic%20editorial%20photography%2C%20detailed%20texture&width=1400&height=900&seq=contact-hero-01&orientation=landscape"
        imageAlt="A quiet Islington flat hallway"
        primaryLabel="Send a message"
        primaryTo="#contact-form"
        secondaryLabel="Book Appointment"
        secondaryTo="/book"
      />

      <section className="px-4 md:px-8 py-12 sm:py-16 md:py-24 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 md:gap-10">
          <div className="lg:col-span-5 space-y-4 order-2 lg:order-1">
            <div className="p-5 sm:p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200">
              <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-600">
                Direct contact
              </p>
              <div className="mt-5 space-y-4">
                <a
                  href="tel:+447700900123"
                  className="group flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-accent-500 transition-colors cursor-pointer"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-950 text-background-50 group-hover:bg-accent-500 group-hover:text-primary-950 transition-colors">
                    <i className="ri-phone-fill text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">Call</p>
                    <p className="text-base font-semibold text-foreground-950">07700 900123</p>
                  </div>
                  <i className="ri-arrow-right-up-line ml-auto text-foreground-500"></i>
                </a>
                <a
                  href="https://wa.me/447700900123"
                  className="group flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-accent-500 transition-colors cursor-pointer"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-emerald-500 text-background-50">
                    <i className="ri-whatsapp-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">WhatsApp</p>
                    <p className="text-base font-semibold text-foreground-950">Send a quick note</p>
                  </div>
                  <i className="ri-arrow-right-up-line ml-auto text-foreground-500"></i>
                </a>
                <a
                  href="mailto:hello@wattsandwire.co.uk"
                  className="group flex items-center gap-4 p-4 rounded-xl bg-background-50 border border-background-200 hover:border-accent-500 transition-colors cursor-pointer"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-950 text-background-50 group-hover:bg-accent-500 group-hover:text-primary-950 transition-colors">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">Email</p>
                    <p className="text-base font-semibold text-foreground-950">
                      hello@wattsandwire.co.uk
                    </p>
                  </div>
                  <i className="ri-arrow-right-up-line ml-auto text-foreground-500"></i>
                </a>
              </div>
            </div>

            <div className="p-5 sm:p-6 rounded-2xl bg-background-100 border border-background-200">
              <p className="text-[11px] uppercase tracking-[0.18em] text-foreground-600 mb-3">
                Working hours
              </p>
              <ul className="text-sm space-y-2 text-foreground-800">
                <li className="flex justify-between"><span>Mon–Fri</span><span>08:00 – 18:00</span></li>
                <li className="flex justify-between"><span>Saturday</span><span>09:00 – 14:00</span></li>
                <li className="flex justify-between"><span>Sunday</span><span className="text-foreground-500">Emergency only</span></li>
              </ul>
              <div className="mt-4 p-3 rounded-lg bg-primary-950 text-background-100 text-xs leading-relaxed">
                Emergency? If it's an immediate safety issue, turn off the power and call emergency services first.
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-background-200">
              <iframe
                title="Islington service area map"
                src="https://www.google.com/maps?q=Upper%20Street%2C%20Islington%2C%20London&t=&z=14&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="260"
                loading="lazy"
                style={{ border: 0 }}
              ></iframe>
            </div>
          </div>

          <div id="contact-form" className="lg:col-span-7 order-1 lg:order-2">
            {status === "success" ? (
              <div className="rounded-2xl bg-background-100 border border-background-200 p-6 sm:p-8 md:p-12 text-center fade-up">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-500 text-primary-950">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h2 className="mt-5 text-xl sm:text-2xl md:text-4xl font-heading font-semibold text-foreground-950 tracking-tight">
                  Thanks — message received.
                </h2>
                <p className="mt-3 text-sm md:text-base text-foreground-600">
                  I'll get back to you as soon as I can, usually within a few hours during working hours.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-2xl bg-background-50 border border-background-200 p-5 sm:p-6 md:p-10 space-y-6"
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <div>
                  <h2 className="text-xl sm:text-2xl md:text-4xl font-heading font-semibold text-foreground-950 tracking-tight leading-tight">
                    Send a message
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    A few short details help me get back to you quickly.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-[0.14em] text-foreground-600">Name</span>
                    <input
                      type="text"
                      name="name"
                      required
                      maxLength={80}
                      className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-[0.14em] text-foreground-600">Phone</span>
                    <input
                      type="tel"
                      name="phone"
                      required
                      maxLength={30}
                      placeholder="07…"
                      className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-[0.14em] text-foreground-600">Email</span>
                    <input
                      type="email"
                      name="email"
                      required
                      maxLength={120}
                      placeholder="you@example.co.uk"
                      className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs uppercase tracking-[0.14em] text-foreground-600">Postcode</span>
                    <input
                      type="text"
                      name="postcode"
                      required
                      maxLength={12}
                      placeholder="N1 9AB"
                      className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    />
                  </label>
                  <label className="flex flex-col gap-2 sm:col-span-2">
                    <span className="text-xs uppercase tracking-[0.14em] text-foreground-600">Preferred contact method</span>
                    <select
                      name="preferred_contact"
                      required
                      defaultValue="Phone call"
                      className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all"
                    >
                      {contactMethods.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-2 sm:col-span-2">
                    <span className="text-xs uppercase tracking-[0.14em] text-foreground-600">Message</span>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="What do you need help with?"
                      className="px-4 py-3 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                    ></textarea>
                    <span className="text-[11px] text-foreground-500 text-right">
                      Up to 500 characters
                    </span>
                  </label>
                  <label className="flex flex-col gap-2 sm:col-span-2">
                    <span className="text-xs uppercase tracking-[0.14em] text-foreground-600">Upload photo (optional)</span>
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-dashed border-background-300">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50 text-accent-700">
                        <i className="ri-image-add-line text-lg"></i>
                      </span>
                      <input
                        type="file"
                        name="photo"
                        accept="image/*"
                        className="flex-1 text-sm text-foreground-800 file:mr-4 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-primary-950 file:text-background-50 cursor-pointer"
                      />
                    </div>
                  </label>
                </div>

                {status === "error" && formError && (
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800">
                    <i className="ri-error-warning-line text-lg mt-0.5"></i>
                    <p>{formError}</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  <p className="text-xs text-foreground-500">
                    Your details won't be used for marketing.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="group inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5 sm:py-4 rounded-full bg-primary-950 text-background-50 text-sm font-semibold hover:bg-primary-900 transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i> Sending…
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}